import axios from 'axios';

import { post } from '@/shared/utils/FetchV2.util';
import type { IvaTreatment } from '../constants/ivaTreatment';
import { fiscalEngineOptions } from './fiscalEngineLocal';

/**
 * El asistente de facturación de `heru-fiscal-engine`
 * (`internal/modules/invoiceassistant`). Propone un borrador con el estado de
 * cada campo; no timbra ni crea nada.
 *
 * ⚠️ Huma rechaza con 422 cualquier campo de más, y un `null` donde el esquema
 * pide cadena. Lo opcional se OMITE, no se manda en `null`.
 */
const TURN_ENDPOINT = '/web/invoice-assistant/turn';
const outcomeEndpoint = (sessionId: string) =>
  `/web/invoice-assistant/sessions/${sessionId}/outcome`;

/** Tope del back: el historial completo viaja en cada turno, pero no más de esto. */
export const ASSISTANT_MAX_MESSAGES = 10;
/** Un mensaje más corto es un 400. */
export const ASSISTANT_MIN_MESSAGE_LENGTH = 3;

export type AssistantFieldStatus = 'rule' | 'user' | 'inferred' | 'missing' | 'pending_taxes';

export interface AssistantMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface AssistantReceiverDraft {
  rfc?: string | null;
  name?: string | null;
  tax_regime?: string | null;
  postal_code?: string | null;
  usocfdi?: string | null;
  residencia_fiscal?: string | null;
  num_reg_id_trib?: string | null;
}

export interface AssistantConceptDraft {
  product_code?: string | null;
  candidates_product_code?: string[];
  unit_code?: string | null;
  quantity?: number | null;
  description: string;
  unit_price?: number | null;
  iva_treatment: IvaTreatment | 'pending_taxes';
  /** Siempre vacío en v1; el esquema lo exige igual. */
  retentions: [];
}

export interface AssistantDraft {
  data: {
    invoice_type?: string | null;
    payment_form?: string | null;
    payment_method?: string | null;
    place_of_expedition?: string | null;
    currency?: string | null;
    exchange_rate?: number | null;
    receiver?: AssistantReceiverDraft;
  };
  concepts: AssistantConceptDraft[];
  /** Llave = ruta del campo: `data.receiver.rfc`, `concepts[0].unit_price`. */
  field_status?: Record<string, AssistantFieldStatus>;
}

export interface AssistantQuestion {
  field: string;
  prompt: string;
  type: 'text' | 'number' | 'choice' | 'email';
  min?: number;
  max?: number;
  optional?: boolean;
  options?: { value: string; label: string }[];
}

export interface AssistantTurn {
  session_id: string;
  turn_index: number;
  reply: string;
  intent: 'emitir' | 'deducir' | 'declaracion' | 'factura_heru' | 'otro';
  draft: AssistantDraft | null;
  questions: AssistantQuestion[];
  escalations: { to: 'taxes' | 'ops'; rule_id: string; question: string }[];
  needs_human: { reason: 'fiscal' | 'ops'; detail: string } | null;
  /** El modelo, o `fallback` / `csd-gate` / `dedupe` cuando la respuesta es fija. */
  model: string;
}

export interface AssistantTurnRequest {
  session_id?: string;
  messages: AssistantMessage[];
  client_draft?: AssistantDraft;
}

export const sendAssistantTurn = (body: AssistantTurnRequest) =>
  post<AssistantTurn>({
    service: 'fiscalEngine',
    endpoint: TURN_ENDPOINT,
    options: fiscalEngineOptions(TURN_ENDPOINT),
    body,
    skipAccountError: true,
  });

/** Una vez, tras el timbrado de una factura que nació de una sesión. Cierra la medición por uuid. */
export const reportAssistantOutcome = (sessionId: string, invoiceUuid: string) => {
  const endpoint = outcomeEndpoint(sessionId);
  return post({
    service: 'fiscalEngine',
    endpoint,
    options: fiscalEngineOptions(endpoint),
    body: { invoice_uuid: invoiceUuid },
    skipAccountError: true,
  });
};

export interface AssistantError {
  status?: number;
  code?: string;
  message?: string;
}

/** El back manda `{ statusCode, message, code }`; `code` es el contrato estable. */
export const assistantErrorOf = (error: unknown): AssistantError => {
  if (!axios.isAxiosError(error)) return {};
  const body = error.response?.data as { code?: string; message?: string } | undefined;
  return { status: error.response?.status, code: body?.code, message: body?.message };
};
